import type { PaginatedNode } from "./PaginatedNode";
import type { ID, PageRequestKey } from "./types";

/** Structural equality for two PageRequestKey values. */
export function sameRequestKey(a: PageRequestKey, b: PageRequestKey): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === "fromLast" || b.kind === "fromLast") {
    return a.kind === "fromLast" && b.kind === "fromLast";
  }
  return a.cursor === b.cursor;
}

/**
 * Collect all leaf-level items in DFS order.
 * Leaf = node at maxDepth. For flat lists (maxDepth=0), root is the leaf.
 */
export function collectLeafItems<T>(root: PaginatedNode<T>, maxDepth: number): T[] {
  const result: T[] = [];

  function visit(node: PaginatedNode<T>): void {
    if (node.depth >= maxDepth) {
      for (const item of node.pages.items) {
        result.push(item);
      }
      return;
    }
    for (const page of node.pages.pages) {
      for (const childId of page.itemIds) {
        const child = node.getChild(childId);
        if (child) {
          visit(child);
        }
      }
    }
  }

  visit(root);
  return result;
}

/**
 * Collect all branch-level (non-leaf) items in pre-order DFS.
 * Parent items appear before their children — matches Grid's flat list order.
 */
export function collectBranchItems<T>(root: PaginatedNode<T>, maxDepth: number): T[] {
  const result: T[] = [];

  function visit(node: PaginatedNode<T>): void {
    if (node.depth >= maxDepth) {
      return;
    }

    for (const page of node.pages.pages) {
      for (let i = 0; i < page.items.length; i++) {
        const item = page.items[i];
        const childId = page.itemIds[i];
        result.push(item);
        const child = node.getChild(childId);
        if (child && child.depth < maxDepth) {
          visit(child);
        }
      }
    }
  }

  visit(root);
  return result;
}

/**
 * Get all nodes at a specific depth in DFS (document) order.
 */
export function getNodesAtDepth<T>(root: PaginatedNode<T>, depth: number): Array<PaginatedNode<T>> {
  if (depth === 0) {
    return [root];
  }

  const result: Array<PaginatedNode<T>> = [];
  const collect = (node: PaginatedNode<T>): void => {
    for (const page of node.pages.pages) {
      for (const childId of page.itemIds) {
        const child = node.getChild(childId);
        if (!child) {
          continue;
        }
        if (child.depth === depth) {
          result.push(child);
        } else if (child.depth < depth) {
          collect(child);
        }
      }
    }
  };
  collect(root);
  return result;
}

/**
 * First and last absolute child-slot indices (across all pages) whose
 * materialized child has at least one loaded page. Indices count every
 * slot in document order — including unmaterialized or empty slots —
 * so callers can compare them against an `absoluteIdx` running counter.
 */
export function findChildLoadedBoundaries<T>(node: PaginatedNode<T>): {
  firstLoadedIdx: number;
  lastLoadedIdx: number;
} {
  let firstLoadedIdx = -1;
  let lastLoadedIdx = -1;
  let i = -1;
  for (const page of node.pages.pages) {
    for (const childId of page.itemIds) {
      i++;
      const child = node.getChild(childId);
      if (!child || child.pages.isEmpty) {
        continue;
      }
      if (firstLoadedIdx === -1) {
        firstLoadedIdx = i;
      }
      lastLoadedIdx = i;
    }
  }
  return { firstLoadedIdx, lastLoadedIdx };
}

/**
 * Look up the child at an absolute index inside a parent node.
 * Returns the materialized child node (and the slot's id) or `null`
 * if the index is out of range or the slot is unmaterialized.
 */
export function getChildSlotAt<T>(
  node: PaginatedNode<T>,
  idx: number,
): { id: ID; child: PaginatedNode<T> } | null {
  if (idx < 0) {
    return null;
  }
  let i = -1;
  for (const page of node.pages.pages) {
    for (const childId of page.itemIds) {
      i++;
      if (i === idx) {
        const child = node.getChild(childId);
        return child ? { id: childId, child } : null;
      }
    }
  }
  return null;
}

/**
 * Locate `childId` within a parent's pages — returns its `(pageIdx,
 * siblingIdx)` coordinates or null if not found.
 */
export function locateChildInParent<T>(
  parent: PaginatedNode<T>,
  childId: ID,
): { pageIdx: number; siblingIdx: number } | null {
  const pages = parent.pages.pages;
  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const siblingIdx = pages[pageIdx].itemIds.indexOf(childId);
    if (siblingIdx >= 0) {
      return { pageIdx, siblingIdx };
    }
  }
  return null;
}

/**
 * Decides how an empty node should be loaded: `fromLast` (scroll-up)
 * vs forward bootstrap (just-expanded / initial).
 *
 * Returns `true` when the empty node is an earlier sibling reached
 * by scrolling up — load its LAST page first via `fromLast`, glued
 * to the already-loaded zone below.
 *
 * Returns `false` when the node was just expanded by the user (they
 * expect the FIRST page) or when nothing is loaded yet (initial
 * bootstrap) — load forward from the top.
 */
export function isEmptyAwaitingFromLast<T>(
  child: PaginatedNode<T>,
  childIdx: number,
  boundaries: { firstLoadedIdx: number; lastLoadedIdx: number },
): boolean {
  if (!child.pages.isEmpty) {
    return false;
  }
  if (child.justExpanded) {
    return false;
  }
  return boundaries.lastLoadedIdx >= 0 && childIdx < boundaries.lastLoadedIdx;
}

/**
 * Hysteresis edge-hit check used by trim gates.
 *
 * - `side="below"` → `hasNearTopHit` (visible in FIRST `threshold`
 *   records of any loaded page). "User retreated UP past a page top"
 *   → safe to trim below.
 * - `side="above"` → `hasNearBottomHit` (visible in LAST `threshold`
 *   records of any loaded page). "User retreated DOWN past a page
 *   bottom" → safe to trim above.
 */
export function hasEdgeHit<T>(
  side: "above" | "below",
  node: PaginatedNode<T>,
  visibleIds: ReadonlySet<ID>,
  threshold: number,
): boolean {
  return side === "below"
    ? hasNearTopHit(node, visibleIds, threshold)
    : hasNearBottomHit(node, visibleIds, threshold);
}

/**
 * Whether any visible id falls within the first `threshold` items of
 * this node's flat sequence (spans pages — not capped to a single page).
 */
export function hasNearTopHit<T>(
  node: PaginatedNode<T>,
  visibleIds: ReadonlySet<ID>,
  threshold: number,
): boolean {
  let scanned = 0;
  for (const page of node.pages.pages) {
    for (const id of page.itemIds) {
      if (scanned >= threshold) {
        return false;
      }
      if (visibleIds.has(id)) {
        return true;
      }
      scanned++;
    }
  }
  return false;
}

/**
 * Whether any visible id falls within the last `threshold` items of
 * this node's flat sequence (spans pages from the end).
 */
export function hasNearBottomHit<T>(
  node: PaginatedNode<T>,
  visibleIds: ReadonlySet<ID>,
  threshold: number,
): boolean {
  let scanned = 0;
  for (let pageIdx = node.pages.pages.length - 1; pageIdx >= 0; pageIdx--) {
    const ids = node.pages.pages[pageIdx].itemIds;
    for (let i = ids.length - 1; i >= 0; i--) {
      if (scanned >= threshold) {
        return false;
      }
      if (visibleIds.has(ids[i])) {
        return true;
      }
      scanned++;
    }
  }
  return false;
}
